<template>
  <div class="custom-work-item" ref="itemRef" :key="componentKey">
    <div class="image-container">
      <div
        v-if="!imageLoaded && sizeRatio"
        :style="{ paddingTop: sizeRatio, backgroundColor: placeholderColor }"
        class="loader-placeholder"
      ></div>
      <img 
        v-if="!isVimeo && !isVideo && previewImage"
        :src="previewImage" 
        :alt="work.title"
        class="work-image"
        @load="onImageLoad"
        @error="onImageError"
        loading="lazy"
      />
      <video
        v-else-if="!isVimeo && isVideo"
        :src="previewImage"
        class="work-image video-preview"
        @loadedmetadata="onImageLoad"
        @error="onImageError"
        autoplay
        muted
        loop
      ></video>
      <div v-else-if="videoData" class="vimeo-wrapper">
        <div :ref="setVimeoRef(0)"></div>
      </div>
      <div class="metadata">
        <span class="metadata-text">{{ displayText }}</span>
        <span class="metadata-description">{{ displayDescription }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, nextTick, onBeforeUnmount, watch } from 'vue'
import Player from '@vimeo/player'

interface Work {
  _id: string
  title: string
  media?: Array<{ url: string }>
  tag?: string
  user?: {
    name: string
  }
  coverImage?: string
  contentImages?: any[]
}

const props = defineProps<{
  work: Work
}>()

// Initialize all reactive variables first
const itemRef = ref<HTMLElement | null>(null)
const componentKey = ref(0)
const imageLoaded = ref(false)
const sizeRatio = ref('')
const placeholderColor = '#f0f0f0'
const isVimeo = ref(false)
const isVideo = ref(false)
const previewImage = ref('')
const videoData = ref<any>(null)
const vimeoRefs = ref<any[]>([])
const playerInstance = ref<{ [key: string]: Player }>({})
const itemWidth = ref(0)
const itemHeight = ref(0)
const startVal = ref<any>(0)
const endVal = ref<any>(0)
const ACCESS_TOKEN = '2882f8d876e951dfb0357c88629dcf78'

const displayText = computed(() => {
  return props.work.title
})

const displayDescription = computed(() => {
  return props.work.user?.name
})

// Helper functions
const setVimeoRef = (index: number) => (el: any) => {
  if (el) {
    vimeoRefs.value[index] = el
  } else {
    delete vimeoRefs.value[index]
  }
}

const calculateVimeoRatio = (width: number, height: number) => {
  const ratio = (height / width) * 100
  sizeRatio.value = `${ratio}%`
}

const calculateItemWidth = () => {
  if (!itemRef.value) return
  
  const width = itemRef.value.offsetWidth
  const height = itemRef.value.offsetHeight
  
  itemWidth.value = width
  itemHeight.value = height

  if (videoData.value) {
    const videoAspectRatio = videoData.value.width / videoData.value.height
    const containerAspectRatio = width / height

    if (containerAspectRatio > videoAspectRatio) {
      // Container is wider than video
      const newWidth = height * videoAspectRatio
      itemWidth.value = newWidth
      itemHeight.value = height
    } else {
      // Container is taller than video
      const newHeight = width / videoAspectRatio
      itemWidth.value = width
      itemHeight.value = newHeight
    }
  }

  const vimeoContainer = vimeoRefs.value[0]
  if (vimeoContainer) {
    const iframe = vimeoContainer.querySelector('iframe')
    if (iframe) {
      iframe.style.width = `${itemWidth.value}px`
      iframe.style.height = `${itemHeight.value}px`
    }
  }
}

const getVimeoVideoId = (url: string) => {
  const regex = /vimeo\.com\/(?:.*#|.*\/videos\/)?([0-9]+)(?:$|\/|\?)/
  const match = regex.exec(url)
  return match ? match[1] : null
}

const getQueryParams = (url: string) => {
  const urlParams = new URLSearchParams(new URL(url).search)
  const start = urlParams.get('start') || '0'
  const end = urlParams.get('end')
  return { start, end }
}

const extractSizeFromUrl = (url: string) => {
  const regex = /_(\d+)x(\d+)\./
  const match = url.match(regex)
  if (match) {
    const width = parseInt(match[1], 10)
    const height = parseInt(match[2], 10)
    return { width, height }
  }
  return null
}

const calculateSizeRatio = (url: string) => {
  const size = extractSizeFromUrl(url)
  if (size) {
    const ratio = (size.height / size.width) * 100
    sizeRatio.value = `${ratio}%`
  }
}

const onImageLoad = () => {
  imageLoaded.value = true
}

const onImageError = () => {
  console.error('Image failed to load:', previewImage.value)
}

const restartAutoplay = (player: Player) => {
  if (player) {
    player.ready().then(() => {
      player.setCurrentTime(startVal.value).then(() => {
        player.play()
      })

      player.on('timeupdate', (data: any) => {
        if (data.seconds >= endVal.value) {
          player.pause()
          player.setCurrentTime(startVal.value).then(() => {
            player.play()
          })
        }
      })
    })
  }
}

// Now add the watch after all variables and functions are defined
watch(() => props.work, async (newWork) => {
  // Reset state
  imageLoaded.value = false
  isVimeo.value = false
  isVideo.value = false
  previewImage.value = ''
  videoData.value = null
  componentKey.value++ // Increment key to force re-render

  // Clean up previous Vimeo player if exists
  if (playerInstance.value[props.work._id]) {
    playerInstance.value[props.work._id].destroy()
    delete playerInstance.value[props.work._id]
  }

  // Initialize with new work
  if (newWork.coverImage?.includes('vimeo')) {
    try {
      isVimeo.value = true
      const videoId = getVimeoVideoId(newWork.coverImage)
      if (videoId) {
        const response = await fetch(`https://api.vimeo.com/videos/${videoId}`, {
          headers: {
            Authorization: `Bearer ${ACCESS_TOKEN}`
          }
        })
        videoData.value = await response.json()
        const { start, end } = getQueryParams(newWork.coverImage)
        startVal.value = start
        endVal.value = end
        
        await nextTick()
        calculateItemWidth()

        const vimeoRef = vimeoRefs.value[0]
        if (!playerInstance.value[newWork._id] && vimeoRef) {
          const player = new Player(vimeoRef, {
            id: parseInt(videoId),
            autoplay: startVal.value === '0',
            muted: true,
            loop: true,
            autopause: false,
            controls: false,
            width: itemWidth.value,
            height: itemHeight.value,
            responsive: true
          })
          
          player.on('loaded', () => {
            playerInstance.value[newWork._id] = player
            imageLoaded.value = true
            restartAutoplay(player)
          })
        }
        previewImage.value = videoData.value.pictures.sizes[0].link
      }
    } catch (error) {
      console.error('Vimeo API error:', error)
    }
  } else {
    isVideo.value = (newWork.coverImage?.includes('.mp4') || newWork.coverImage?.includes('.mov')) || false
    previewImage.value = newWork.coverImage || newWork.media?.[0]?.url || '/images/placeholder.png'
    calculateSizeRatio(previewImage.value)
  }
}, { immediate: true })

// Clean up on unmount
onBeforeUnmount(() => {
  // Clean up all Vimeo players
  Object.values(playerInstance.value).forEach(player => {
    player.destroy()
  })
  playerInstance.value = {}
})

// Add window resize handler
const handleResize = () => {
  calculateItemWidth()
}

onMounted(async () => {
  if (props.work.coverImage?.includes('vimeo')) {
    try {
      isVimeo.value = true
      const videoId = getVimeoVideoId(props.work.coverImage)
      if (videoId) {
        const response = await fetch(`https://api.vimeo.com/videos/${videoId}`, {
          headers: {
            Authorization: `Bearer ${ACCESS_TOKEN}`
          }
        })
        videoData.value = await response.json()
        const { start, end } = getQueryParams(props.work.coverImage)
        startVal.value = start
        endVal.value = end
        
        await nextTick()
        calculateItemWidth()

        const vimeoRef = vimeoRefs.value[0]
        if (!playerInstance.value[props.work._id] && vimeoRef) {
          const player = new Player(vimeoRef, {
            id: parseInt(videoId),
            autoplay: startVal.value === '0',
            muted: true,
            loop: true,
            autopause: false,
            controls: false,
            width: itemWidth.value,
            height: itemHeight.value,
            responsive: true
          })
          
          player.on('loaded', () => {
            playerInstance.value[props.work._id] = player
            imageLoaded.value = true
            restartAutoplay(player)
          })

          // Add resize event listener
          window.addEventListener('resize', handleResize)
        }
        previewImage.value = videoData.value.pictures.sizes[0].link
      }
    } catch (error) {
      console.error('Vimeo API error:', error)
    }
  } else {
    isVideo.value = (props.work.coverImage?.includes('.mp4') || props.work.coverImage?.includes('.mov')) || false
    previewImage.value = props.work.coverImage || props.work.media?.[0]?.url || '/images/placeholder.png'
    calculateSizeRatio(previewImage.value)
  }
})

// Clean up resize listener
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.custom-work-item {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
  flex: 1;
}

.loader-placeholder {
  width: 100%;
  position: relative;
}

.work-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
}

.vimeo-wrapper {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
}

.vimeo-wrapper iframe {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.metadata {
  display: flex;
  flex-direction: column;
  gap: 0px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  z-index: 1;
}

.metadata-text {
  font-size: 16px;
  font-weight: 500;
}

.metadata-description {
  font-size: 10px;
  font-weight: 500;
}

.video-preview {
  object-fit: cover;
}
</style> 